// FAQPage.js - Premium Design
import React from 'react';
import Layout from '../components/layout/Layout';

const FAQItem = ({ question, answer }) => (
  <details className="group border-b border-white/5 last:border-0 rounded-2xl overflow-hidden mb-4 bg-white/[0.02] hover:bg-white/[0.05] transition-all border border-white/5">
    <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
      <h3 className="text-sm md:text-base font-bold text-white pr-8">{question}</h3>
      <span className="text-blue-500 transition-transform group-open:rotate-180">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    </summary>
    <div className="px-6 pb-6 text-sm md:text-base text-gray-400 leading-relaxed animate-in fade-in slide-in-from-top-2 duration-300">
      {answer}
    </div>
  </details>
);

const FAQPage = () => {
  const faqs = [
    {
      question: "How much does an airport transfer from CMB to Colombo cost?",
      answer: "Airport transfers from Bandaranaike International Airport (CMB) to Colombo hotels start from approximately $42 for an economy sedan. Pricing varies by vehicle class, destination, and time of day. You can see exact prices by entering your destination in the booking form on our homepage."
    },
    {
      question: "How do I get picked up at Colombo Bandaranaike Airport?",
      answer: "After booking, you will receive your driver's details before your flight lands. Your chauffeur will be waiting at the arrivals hall with a name board. We track your flight in real-time, so if your flight is delayed, your driver's timing auto-adjusts — no extra charge."
    },
    {
      question: "Can I book an airport transfer from CMB to Kandy or Galle?",
      answer: "Yes. LankaWayFinder offers airport transfers from CMB to any destination across Sri Lanka, including Kandy (approximately 3.5 hours), Galle (approximately 2.5 hours), Negombo (15 minutes), Ella, Sigiriya, and all hotels islandwide."
    },
    {
      question: "What vehicles are available for airport transfers?",
      answer: "We offer four vehicle classes: Economy Sedan (up to 3 passengers), Standard Class Sedan (up to 3 passengers), Standard Van (up to 7 passengers), and Minibus (up to 12 passengers). All vehicles are air-conditioned, clean, and driven by vetted professional chauffeurs."
    },
    {
      question: "Is there a meet-and-greet service at CMB airport?",
      answer: "Yes. Every airport transfer includes a complimentary meet-and-greet at the arrivals hall. Your driver will hold a visible name board and assist with luggage. This service is included at no additional cost for all vehicle classes."
    },
    {
      question: "Can I cancel or modify my airport transfer booking?",
      answer: "Yes. We offer free cancellation up to 24 hours before your scheduled pickup. Modifications to pickup time, destination, or vehicle class can be made at any time before your transfer by contacting us via WhatsApp, email, or phone."
    },
    {
      question: "Do you offer hourly chauffeur service in Sri Lanka?",
      answer: "Yes. In addition to airport transfers, we offer an hourly chauffeur service across Sri Lanka. This is ideal for city tours, multi-stop itineraries, business meetings, and day trips. Minimum booking is 4 hours."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept major credit cards (Visa, Mastercard, Amex), and bank transfers. All online payments are processed securely. Prices can be viewed in USD, EUR, or LKR."
    },
    {
      question: "How far in advance should I book my airport transfer?",
      answer: "We recommend booking at least 24 hours in advance to guarantee availability, especially during peak travel season (December–March). However, we do accept same-day bookings subject to driver availability."
    },
    {
      question: "Do you provide child seats or booster seats?",
      answer: "Yes. Child seats and booster seats are available upon request at no additional charge. Please mention the child's age when booking so we can arrange the appropriate seat in advance."
    }
  ];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-20 lg:py-32">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16 px-4">
            <span 
              className="text-[10px] md:text-xs font-black uppercase tracking-[0.4em] mb-4 inline-block translate-y-[-10px]"
              style={{ color: 'var(--gold)' }}
            >
              Support Center
            </span>
            <h1 className="text-4xl md:text-7xl font-black text-white uppercase tracking-tighter leading-[0.85] mb-6 drop-shadow-2xl">
              <span className="opacity-40 block text-2xl md:text-4xl mb-2 tracking-[0.1em]">Frequently Asked</span>
              <span className="text-blue-600 block italic">Questions</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-lg font-medium max-w-xl mx-auto leading-relaxed opacity-80">
              Everything you need to know about booking your premium private airport transfer in Sri Lanka.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} {...faq} />
            ))}
          </div>

          <div className="mt-20 p-8 md:p-12 rounded-[2.5rem] bg-blue-600/10 border border-blue-500/20 text-center relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-blue-600/40 transition-all duration-700"></div>
            
            <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter mb-4 relative z-10">Still have questions?</h2>
            <p className="text-gray-400 text-sm font-medium mb-10 relative z-10 max-w-md mx-auto leading-relaxed">
              Our travel concierge team typically responds within 30 minutes via WhatsApp or Email.
            </p>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 relative z-10">
              <a 
                href="mailto:rides@lankawayfinder.com" 
                className="w-full md:w-auto px-10 py-4 bg-blue-600 text-white rounded-full font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-500/20 hover:bg-blue-700 active:scale-95 transition-all"
              >
                Email Support
              </a>
              <a 
                href="https://wa.me/94774052202" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-full md:w-auto px-10 py-4 bg-white/5 border border-white/10 text-white rounded-full font-black text-xs uppercase tracking-widest hover:bg-white/10 transition-all"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FAQPage;
